import { z } from 'zod';

/**
 * Client-side form schemas.
 *
 * These mirror the API's schemas but are intentionally *not* shared with it.
 * The server must validate independently — a browser can send anything — and
 * the two have different jobs: this one exists to give fast, friendly feedback
 * before a request is made.
 *
 * Password complexity is deliberately not duplicated here: the real rules live
 * in `security.policy` and are editable at runtime, so a hard-coded copy would
 * drift and start rejecting passwords the server would accept.
 */
export const loginFormSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginFormValues = z.infer<typeof loginFormSchema>;

export const forgotPasswordFormSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
});
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordFormSchema>;

export const resetPasswordFormSchema = z
  .object({
    newPassword: z.string().min(1, 'New password is required'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
export type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>;

export const changePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(1, 'New password is required'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  })
  .refine((values) => values.currentPassword !== values.newPassword, {
    path: ['newPassword'],
    message: 'New password must differ from the current one',
  });
export type ChangePasswordFormValues = z.infer<typeof changePasswordFormSchema>;
